import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import { previewMeta, updateMeta } from '../../../redux/features/landingCmsSlice';
import ImageField from '../questionbank/ImageField';

/**
 * Meta tags and structured data — the page's entire organic-search surface.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS SCREEN IS A PREVIEW AND NOT A FORM
 *
 * Nobody edits a meta description because they want a meta description. They
 * edit it because they want a better-looking search result. So the left half is
 * the fields and the right half is what Google and a shared WhatsApp link will
 * actually render, updated as they type — including the truncation, which is
 * the single thing a character counter cannot convey.
 *
 * The counters are still there, and they turn amber rather than red past the
 * recommended length, because those lengths are display heuristics and not
 * rules. Google indexes the whole string either way, and a marketer who knows
 * that should not be blocked by a form.
 *
 * ---------------------------------------------------------------------------
 * WHAT DOES BLOCK
 *
 * Only things that are actually broken and would fail silently: a relative
 * canonical URL (resolved against the crawler's host, not ours), a relative
 * og:image (Facebook fetches it from their servers and cannot reach a path),
 * and JSON-LD that does not parse (Google drops it with no visible sign).
 */

const LENGTH_BAR = (value, max) => Math.min(100, Math.round((value / max) * 100));

const SeoPanel = ({ meta, preview: savedPreview, options, revalidateSeconds }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { seo, saving } = useSelector((state) => state.landingCms);

  const [form, setForm] = useState(() => ({
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    canonicalUrl: '',
    robots: 'index, follow',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    ogImageAlt: '',
    ogType: 'website',
    ogSiteName: '',
    twitterCard: 'summary_large_image',
    twitterSite: '',
    jsonLd: '',
  }));

  useEffect(() => {
    if (!meta) return;
    setForm({
      metaTitle: meta.metaTitle || '',
      metaDescription: meta.metaDescription || '',
      metaKeywords: meta.metaKeywords || '',
      canonicalUrl: meta.canonicalUrl || '',
      robots: meta.robots || 'index, follow',
      ogTitle: meta.ogTitle || '',
      ogDescription: meta.ogDescription || '',
      ogImage: meta.ogImage || '',
      ogImageAlt: meta.ogImageAlt || '',
      ogType: meta.ogType || 'website',
      ogSiteName: meta.ogSiteName || '',
      twitterCard: meta.twitterCard || 'summary_large_image',
      twitterSite: meta.twitterSite || '',
      // Pretty-printed so it is editable by a human rather than one long line.
      jsonLd: meta.jsonLd ? JSON.stringify(meta.jsonLd, null, 2) : '',
    });
  }, [meta]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // Debounced dry run. The server owns the rules; re-implementing the length
  // and URL checks here would give two sources of truth that drift.
  useEffect(() => {
    const timer = setTimeout(() => dispatch(previewMeta(form)), 450);
    return () => clearTimeout(timer);
  }, [form, dispatch]);

  const preview = seo.preview || savedPreview;
  const lengths = preview?.lengths;

  const save = async () => {
    try {
      await dispatch(updateMeta(form)).unwrap();
      enqueueSnackbar(
        `Meta tags saved. Live on the page within ${revalidateSeconds} seconds.`,
        { variant: 'success', autoHideDuration: 6000 }
      );
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not save the meta tags', { variant: 'error' });
    }
  };

  const counter = (label, value, max) => {
    if (!max) return null;
    const over = value > max;
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
        <LinearProgress
          variant="determinate"
          value={LENGTH_BAR(value, max)}
          color={over ? 'warning' : 'primary'}
          sx={{ flex: 1, height: 5, borderRadius: 3 }}
        />
        <Typography variant="caption" color={over ? 'warning.main' : 'text.secondary'}>
          {value}/{max}
        </Typography>
      </Stack>
    );
  };

  return (
    <Grid container spacing={2}>
      {/* ------------------------------ fields ------------------------------ */}
      <Grid item xs={12} md={7}>
        <Stack spacing={2}>
          {seo.errors.length > 0 && (
            <Alert severity="error">
              <AlertTitle>Fix before saving</AlertTitle>
              <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.5}>
                {seo.errors.map((e) => (
                  <li key={e}>
                    <Typography variant="body2">{e}</Typography>
                  </li>
                ))}
              </Stack>
            </Alert>
          )}

          {seo.warnings.length > 0 && (
            <Alert severity="warning">
              <AlertTitle>Suggestions</AlertTitle>
              <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.5}>
                {seo.warnings.map((w) => (
                  <li key={w}>
                    <Typography variant="body2">{w}</Typography>
                  </li>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                These do not block saving.
              </Typography>
            </Alert>
          )}

          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Search result
            </Typography>

            <TextField
              fullWidth
              size="small"
              label="Page title"
              value={form.metaTitle}
              onChange={(e) => set({ metaTitle: e.target.value })}
              sx={{ mt: 1 }}
            />
            {counter('title', lengths?.title ?? form.metaTitle.length, lengths?.titleMax ?? 60)}

            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              label="Description"
              value={form.metaDescription}
              onChange={(e) => set({ metaDescription: e.target.value })}
              sx={{ mt: 2 }}
            />
            {counter(
              'description',
              lengths?.description ?? form.metaDescription.length,
              lengths?.descriptionMax ?? 160
            )}

            <TextField
              fullWidth
              size="small"
              label="Canonical URL"
              placeholder="https://www.intervaledu.com/auth/parent-landing"
              value={form.canonicalUrl}
              onChange={(e) => set({ canonicalUrl: e.target.value })}
              sx={{ mt: 2 }}
              helperText="Must be absolute. Tells search engines which URL is the real one."
            />

            <TextField
              select
              fullWidth
              size="small"
              label="Search engine visibility"
              value={form.robots}
              onChange={(e) => set({ robots: e.target.value })}
              sx={{ mt: 2 }}
              helperText={
                form.robots.startsWith('noindex')
                  ? 'This page will be dropped from search results.'
                  : 'This page can appear in search results.'
              }
            >
              {(options?.robots || ['index, follow']).map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              size="small"
              label="Keywords"
              value={form.metaKeywords}
              onChange={(e) => set({ metaKeywords: e.target.value })}
              sx={{ mt: 2 }}
              helperText="Ignored by Google since 2009. Kept because other crawlers read it."
            />
          </Card>

          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Shared links
            </Typography>
            <Typography variant="caption" color="text.secondary">
              What WhatsApp, Facebook and LinkedIn show when someone shares this page.
            </Typography>

            <TextField
              fullWidth
              size="small"
              label="Share title"
              placeholder="Leave empty to use the page title"
              value={form.ogTitle}
              onChange={(e) => set({ ogTitle: e.target.value })}
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Share description"
              placeholder="Leave empty to use the page description"
              value={form.ogDescription}
              onChange={(e) => set({ ogDescription: e.target.value })}
              sx={{ mt: 2 }}
            />

            <Box sx={{ mt: 2 }}>
              <ImageField
                size={72}
                folderName="landing/og"
                label="Share image URL"
                value={form.ogImage}
                onChange={(url) => set({ ogImage: url })}
                helperText="Absolute URL, 1200×630. Facebook fetches this from their own servers."
              />
            </Box>

            <TextField
              fullWidth
              size="small"
              label="Share image alt text"
              value={form.ogImageAlt}
              onChange={(e) => set({ ogImageAlt: e.target.value })}
              sx={{ mt: 2 }}
              helperText="Read aloud by screen readers on shared cards."
            />

            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Twitter card"
                  value={form.twitterCard}
                  onChange={(e) => set({ twitterCard: e.target.value })}
                >
                  {(options?.twitterCards || ['summary_large_image']).map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Twitter handle"
                  placeholder="@intervaledu"
                  value={form.twitterSite}
                  onChange={(e) => set({ twitterSite: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Site name"
                  value={form.ogSiteName}
                  onChange={(e) => set({ ogSiteName: e.target.value })}
                />
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">Structured data</Typography>
              <Tooltip title="schema.org JSON-LD. Tells search engines what this organisation is, which is what powers rich results.">
                <Icon icon="mdi:information-outline" width={16} />
              </Tooltip>
            </Stack>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={8}
              value={form.jsonLd}
              onChange={(e) => set({ jsonLd: e.target.value })}
              sx={{ mt: 1, '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
              helperText="Must be valid JSON. Google silently ignores a block it cannot parse."
            />
          </Card>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              onClick={save}
              disabled={saving || seo.errors.length > 0}
              startIcon={<Icon icon="mdi:content-save" width={18} />}
            >
              {saving ? 'Saving…' : 'Save meta tags'}
            </Button>
            {seo.checking && <CircularProgress size={16} />}
            <Typography variant="caption" color="text.secondary">
              Live on the page within {revalidateSeconds}s of saving.
            </Typography>
          </Stack>
        </Stack>
      </Grid>

      {/* ----------------------------- previews ----------------------------- */}
      <Grid item xs={12} md={5}>
        <Box sx={{ position: 'sticky', top: 16 }}>
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Google result
            </Typography>
            {preview?.google?.noindex && (
              <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
                Hidden from search — this preview is what would show if it were not.
              </Alert>
            )}
            {/* Deliberately styled to look like a real SERP entry rather than
                like the rest of the admin. The point is recognition. */}
            <Box sx={{ fontFamily: 'arial, sans-serif' }}>
              <Typography sx={{ fontSize: 12, color: '#202124' }}>
                {preview?.google?.url || 'www.example.com/page'}
              </Typography>
              <Typography
                sx={{ fontSize: 20, lineHeight: '26px', color: '#1a0dab', mt: 0.25 }}
              >
                {preview?.google?.title || 'Your page title'}
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: '22px', color: '#4d5156', mt: 0.5 }}>
                {preview?.google?.description || 'Your page description will appear here.'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              {preview?.google?.titleTruncated && (
                <Chip size="small" color="warning" variant="outlined" label="Title cut off" />
              )}
              {preview?.google?.descriptionTruncated && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label="Description cut off"
                />
              )}
            </Stack>
          </Card>

          <Card sx={{ p: 0, overflow: 'hidden' }}>
            <Typography variant="subtitle2" sx={{ p: 2, pb: 1 }}>
              Shared link
            </Typography>
            <Box sx={{ border: '1px solid', borderColor: 'divider', m: 2, mt: 0, borderRadius: 1 }}>
              {preview?.social?.image ? (
                <Box
                  component="img"
                  src={preview.social.image}
                  alt={preview.social.imageAlt || ''}
                  sx={{
                    width: '100%',
                    aspectRatio: '1200 / 630',
                    objectFit: 'cover',
                    display: 'block',
                    bgcolor: 'background.neutral',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    aspectRatio: '1200 / 630',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.neutral',
                    color: 'text.disabled',
                  }}
                >
                  <Stack alignItems="center" spacing={0.5}>
                    <Icon icon="mdi:image-off-outline" width={28} />
                    <Typography variant="caption">No share image</Typography>
                  </Stack>
                </Box>
              )}
              <Box sx={{ p: 1.5, bgcolor: 'background.neutral' }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                  {preview?.social?.siteName || 'intervaledu.com'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                  {preview?.social?.title || 'Your page title'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {preview?.social?.description || 'Your page description.'}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SeoPanel;
